/**
 * Translate block (handoff §5.2, §4.3).
 *
 * Segmented control over five targets — Requirements → Pseudocode → SQL → Python → DAX,
 * Requirements default (§4.3: "the safe default … least model-risk output — every downstream
 * target derives from it"). Actions: Copy · Regenerate · Download .md · Adversarial Review.
 *
 * Ryan, 2026-07-17 → confirmed in the subtask brief: translation is LD-native (backend
 * translateLogic.js), not routed out to AI Council. That routing is dead, so the
 * "Send to AI Council" handoff is removed. Two consequences here:
 *   - Regenerate hits LD's own endpoint (POST §4.3), which runs generate → adversarial review
 *     → rewrite. The handoff's "server calls the codegen path" wording is stale.
 *   - The backend can return a translation its review could not confirm. The panel is
 *     human-reviewable by design, so that state is SHOWN with its open issues rather than
 *     hidden — a silently-wrong translation is the failure mode that matters here.
 *
 * Adversarial Review is the surfaced control Ryan asked for: it re-runs the review→rewrite
 * validation (translateLogic.js) on the shown translation on demand — the same check that
 * generation runs implicitly, now a first-class action. A clean pass is confirmed; found
 * issues drop the translation into the needs-human-review state below.
 */

import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { Copy, RefreshCw, Download, ShieldAlert, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { color, font, radius } from '../tokens';
import type { ArtifactDetail, TranslateTab, Translation } from '../types';
import { TRANSLATE_TABS } from '../types';

const LABEL: Record<TranslateTab, string> = {
  requirements: 'Requirements',
  pseudocode: 'Pseudocode',
  sql: 'SQL',
  python: 'Python',
  dax: 'DAX',
};

const COPIED_MS = 1500;   // §5.2: 'Copy … animates "Copied ✓" for 1.5s'

interface Props {
  artifact: ArtifactDetail;
  translateTab: TranslateTab;
  onTranslateTab: (t: TranslateTab) => void;
  onToast: (msg: string) => void;
  /** Injected once §4.3 exists. Without it the block renders the not-yet-generated state. */
  fetchTranslation?: (id: string, target: TranslateTab, regenerate?: boolean) => Promise<Translation>;
  /** Injected once the review endpoint exists. Re-runs the review→rewrite validation
   * (translateLogic.js) on the SHOWN translation — the backend needs the current
   * translation to review it, so it is passed through rather than re-fetched. */
  reviewTranslation?: (id: string, target: TranslateTab, current: Translation) => Promise<Translation>;
  /** Store a generated translation with its run so a reload does not cost another model run. */
  onPersist?: (runId: string, target: TranslateTab, translation: Translation) => void;
  /** True when the parent knows a generation for THIS artifact + target is still running even
   *  though this component instance just mounted fresh (e.g. the user switched away from the
   *  Business Logic tab mid-generation and came back — Ryan 7/31 QA finding 2). Without this,
   *  a remount has no way to tell "nothing was ever generated" apart from "still generating,
   *  ask again in a second," and showed the former for both. */
  pending?: boolean;
}

export default function TranslateBlock({
  artifact, translateTab, onTranslateTab, onToast, fetchTranslation, reviewTranslation, onPersist, pending,
}: Props) {
  const [copyLabel, setCopyLabel] = useState('Copy');
  // Held PER TARGET. Each one costs a generate + adversarial review + possible rewrite — minutes
  // of model time — so switching to SQL and back must not throw the Requirements away and read
  // as though nothing was ever generated.
  const [byTarget, setByTarget] = useState<Partial<Record<TranslateTab, Translation>>>(artifact.translations || {});
  const [cleanByTarget, setCleanByTarget] = useState<Partial<Record<TranslateTab, boolean>>>({});
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  // A different artifact means different logic — never show one file's translation under another,
  // and seed from whatever that run already has stored.
  useEffect(() => {
    setByTarget(artifact.translations || {});
    setCleanByTarget({});
  }, [artifact.id, artifact.translations]);

  const translation = byTarget[translateTab] || null;
  const reviewedClean = !!cleanByTarget[translateTab];
  const setTranslation = (t: Translation | null) =>
    setByTarget((m) => ({ ...m, [translateTab]: t || undefined }));
  const setReviewedClean = (v: boolean) =>
    setCleanByTarget((m) => ({ ...m, [translateTab]: v }));

  const run = async (regenerate: boolean) => {
    if (!fetchTranslation) {
      onToast('Translation endpoint not wired yet');
      return;
    }
    setLoading(true);
    setReviewedClean(false);
    try {
      const result = await fetchTranslation(artifact.id, translateTab, regenerate);
      setTranslation(result);
      if (artifact.run_id && onPersist) onPersist(artifact.run_id, translateTab, result);
    } catch (e: unknown) {
      // Surface what actually went wrong. A generic message here cost hours: the endpoint was
      // returning a real error and the panel reported only "try again".
      const err = e as { response?: { status?: number; data?: { error?: string } }; message?: string };
      const detail = err?.response?.data?.error || err?.message || 'unknown error';
      const status = err?.response?.status;
      console.error('[Translate] failed', { target: translateTab, status, detail, error: e });
      onToast(`Translation failed${status ? ` (${status})` : ''} — ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const onReview = async () => {
    if (!reviewTranslation) {
      onToast('Adversarial review not wired yet');
      return;
    }
    if (!translation) {
      onToast('Generate a translation first');
      return;
    }
    setReviewing(true);
    setReviewedClean(false);
    try {
      const reviewed = await reviewTranslation(artifact.id, translateTab, translation);
      setTranslation(reviewed);
      if (reviewed.needs_human_review) {
        onToast('Adversarial review found issues — see below');
      } else {
        setReviewedClean(true);
        onToast('Adversarial review passed');
      }
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { error?: string } }; message?: string };
      const detail = err?.response?.data?.error || err?.message || 'unknown error';
      console.error('[AdversarialReview] failed', { target: translateTab, detail, error: e });
      onToast(`Adversarial review failed — ${detail}`);
    } finally {
      setReviewing(false);
    }
  };

  const plainText = () =>
    translation ? new DOMParser().parseFromString(translation.translation_html, 'text/html').body.textContent || '' : '';

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainText());
      setCopyLabel('Copied ✓');
      setTimeout(() => setCopyLabel('Copy'), COPIED_MS);
    } catch {
      onToast('Could not copy to clipboard');
    }
  };

  const onDownload = () => {
    const blob = new Blob([plainText()], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${artifact.name}.${translateTab}.md`;
    a.click();
    URL.revokeObjectURL(url);
    onToast('Download started');
  };

  // A remount that's still pending (see the `pending` prop doc) must read as "generating," not
  // as "nothing here yet" — otherwise the fix is just moving where the false negative shows up.
  const effectiveLoading = loading || !!pending;
  const hasContent = !!translation && !effectiveLoading;

  return (
    <div style={{ border: `1px solid ${color.cardBorder}`, borderRadius: radius.card, overflow: 'hidden' }}>
      <div style={{ padding: '12px 14px', background: 'linear-gradient(135deg,#f7f5ff,#f2f4ff)', borderBottom: `1px solid ${color.cardBorder}` }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: color.ink }}>Translate logic</div>
        <div style={{ fontSize: 12, color: color.textMuted, marginTop: 2 }}>
          Regenerable · human-reviewable · downstream to codegen
        </div>
      </div>

      {/* Segmented control — order is contractual (§5.2) */}
      <div style={{ display: 'flex', gap: 3, padding: 10, borderBottom: `1px solid ${color.cardBorder}`, flexWrap: 'wrap' }}>
        {TRANSLATE_TABS.map((t) => {
          const active = translateTab === t;
          return (
            <button
              key={t}
              onClick={() => onTranslateTab(t)}
              style={{
                padding: '5px 11px', borderRadius: radius.chip, cursor: 'pointer',
                border: `1px solid ${active ? color.purple : color.cardBorder}`,
                background: active ? '#f5f3ff' : color.surface,
                color: active ? color.purple : color.textMuted,
                fontFamily: font.ui, fontSize: 12, fontWeight: 600,
              }}
            >
              {LABEL[t]}
            </button>
          );
        })}
      </div>

      {/* Output */}
      <div style={{ background: color.ink, padding: 14, minHeight: 96 }}>
        {effectiveLoading && <div style={{ color: '#8d8bb0', fontSize: 12 }}>Generating {LABEL[translateTab]}…</div>}
        {!effectiveLoading && !translation && (
          <div style={{ color: '#8d8bb0', fontSize: 12, lineHeight: 1.6 }}>
            No {LABEL[translateTab]} generated yet for this unit.
            <br />
            Use Regenerate to produce it.
          </div>
        )}
        {hasContent && (
          <div
            style={{ fontFamily: font.mono, fontSize: 12, color: '#e6e6f2', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(translation!.translation_html) }}
          />
        )}
      </div>

      {/* A clean adversarial-review pass is confirmed to the reviewer. */}
      {hasContent && reviewedClean && !translation!.needs_human_review && (
        <div style={{ display: 'flex', gap: 9, padding: '10px 14px', background: '#f0fdf4', borderTop: '1px solid #bbf7d0' }}>
          <CheckCircle2 size={14} style={{ color: '#15803d', flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12, color: '#14532d', lineHeight: 1.5 }}>
            <b>Adversarial review passed</b> — the automated check confirmed this matches the source logic.
          </div>
        </div>
      )}

      {/* An unconfirmed translation is surfaced, not swallowed. */}
      {hasContent && translation!.needs_human_review && (
        <div style={{ display: 'flex', gap: 9, padding: '10px 14px', background: '#fffbeb', borderTop: '1px solid #fde68a' }}>
          <AlertTriangle size={14} style={{ color: '#b45309', flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12, color: '#78350f', lineHeight: 1.5 }}>
            <b>Needs human review</b> — the automated check could not confirm this matches the source logic.
            {!!translation!.review_issues?.length && (
              <ul style={{ margin: '5px 0 0', paddingLeft: 16 }}>
                {translation!.review_issues!.map((i, n) => (
                  <li key={n} style={{ marginBottom: 2 }}>
                    <b>{i.severity}</b> — {i.claim}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 7, padding: 10, flexWrap: 'wrap', background: '#fbfbfe' }}>
        <Action icon={<Copy size={13} />} label={copyLabel} onClick={onCopy} disabled={!hasContent} />
        <Action icon={<RefreshCw size={13} />} label="Regenerate" onClick={() => run(true)} disabled={effectiveLoading} />
        <Action icon={<Download size={13} />} label="Download .md" onClick={onDownload} disabled={!hasContent} />
        <Action icon={<ShieldAlert size={13} />} label={reviewing ? 'Reviewing…' : 'Adversarial Review'} onClick={onReview} disabled={!hasContent || reviewing} primary />
      </div>

      {hasContent && (
        <div style={{ padding: '0 12px 10px', fontSize: 11, color: color.textMuted }}>
          Generated {new Date(translation!.generated_at).toLocaleString()} · {translation!.model}
        </div>
      )}
    </div>
  );
}

function Action({
  icon, label, onClick, disabled, primary,
}: { icon: React.ReactNode; label: string; onClick: () => void; disabled?: boolean; primary?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '7px 11px', borderRadius: radius.control,
        border: `1px solid ${primary && !disabled ? color.purple : color.cardBorder}`,
        background: primary && !disabled ? color.purple : color.surface,
        color: disabled ? '#b3b2c6' : primary ? '#fff' : color.ink,
        fontFamily: font.ui, fontSize: 12, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {icon}
      {label}
    </button>
  );
}
